import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const BackendErrorHandler = ({ children }) => {
  const [backendError, setBackendError] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [lastErrorTime, setLastErrorTime] = useState(0);

  // Listen for backend connection errors
  useEffect(() => {
    const handleBackendError = (event) => {
      if (event.detail && event.detail.type === 'BACKEND_ERROR') {
        const now = Date.now();
        
        // Only show error if it's been more than 5 seconds since last error
        if (now - lastErrorTime > 5000) {
          setBackendError(true);
          setErrorCount(prev => prev + 1);
          setLastErrorTime(now);
        }
      }
    };

    const handleBackendSuccess = (event) => {
      if (event.detail && event.detail.type === 'BACKEND_SUCCESS') {
        setBackendError(false);
      }
    };

    window.addEventListener('backend-error', handleBackendError);
    window.addEventListener('backend-success', handleBackendSuccess);

    return () => {
      window.removeEventListener('backend-error', handleBackendError);
      window.removeEventListener('backend-success', handleBackendSuccess);
    };
  }, [lastErrorTime]);

  // Periodic health check when backend error is shown
  useEffect(() => {
    let healthCheckInterval;
    
    if (backendError) {
      healthCheckInterval = setInterval(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/health`)
          .then(response => {
            if (response.ok) {
              // Backend is back online
              setBackendError(false);
              window.dispatchEvent(new CustomEvent('backend-success', {
                detail: { type: 'BACKEND_SUCCESS', timestamp: Date.now() }
              }));
            }
          })
          .catch(() => {
            // Backend still down, continue showing error
          });
      }, 10000); // Check every 10 seconds
    }

    return () => {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }
    };
  }, [backendError]);

  const handleRetry = () => {
    setBackendError(false);
    // Trigger a test request to check if backend is back
    window.dispatchEvent(new CustomEvent('retry-backend-connection'));
    
    // Test backend connection
            fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/health`)
      .then(response => {
        if (response.ok) {
          // Backend is back online
          window.dispatchEvent(new CustomEvent('backend-success', {
            detail: { type: 'BACKEND_SUCCESS', timestamp: Date.now() }
          }));
        }
      })
      .catch(() => {
        // Backend still down, show error again after 3 seconds
        setTimeout(() => {
          setBackendError(true);
        }, 3000);
      });
  };

  return (
    <>
      {children}
      
      <AnimatePresence>
        {backendError && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 text-center"
            >
              {/* Error Icon */}
              <div className="mx-auto mb-4 w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>

              {/* Error Message */}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Backend Connection Lost
              </h3>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                We're having trouble connecting to our servers. This might be due to:
              </p>

              <div className="text-left mb-6 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>Server maintenance</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>Network connectivity issues</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>Temporary service disruption</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={handleRetry}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry Connection
                </button>
                
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Refresh Page
                </button>
              </div>

              {/* Error Count */}
              {errorCount > 1 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  Connection attempts: {errorCount}
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BackendErrorHandler; 